import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import DocumentTitle from "react-document-title";

import categories, { findCategory } from "../../data/faqs/index";
import { searchArticles } from "../../modules/search";
import { trackHelpSearch } from "../../lib/analytics";
import { getCsWhatsAppUrl } from "../../lib/contact";
import Breadcrumbs from "../../components/Help/Breadcrumbs";
import HelpSearch from "../../components/Help/HelpSearch";
import Snippet from "../../components/Help/Snippet";
import { ArrowRightIcon } from "../../components/Help/icons";

const searchPath = (keyword, categorySlug) => {
  const params = new URLSearchParams({ keyword });
  if (categorySlug) params.set("category", categorySlug);
  return `/search?${params.toString()}`;
};

/** Categories that have results, in the help center's own order, with counts. */
function facetsFor(results, activeSlug) {
  const counts = {};
  results.forEach(({ article }) => {
    counts[article.category.slug] = (counts[article.category.slug] || 0) + 1;
  });

  return categories
    .filter(category => counts[category.slug] || category.slug === activeSlug)
    .map(category => ({ slug: category.slug, title: category.title, count: counts[category.slug] || 0 }));
}

/**
 * /help/search?keyword=...[&category=...]
 *
 * Laid out like a category page: a rail on the left -- here a filter of the
 * categories the results fall in, with counts -- and the results on the right,
 * each a row with its category, title and a two-line snippet that marks the
 * matched words.
 *
 * `keyword` is the parameter the old app used, so bookmarked and shared
 * search links keep working. `category` comes from the filter or a category
 * page's own search box. The filtered list is searched inside the category
 * rather than cut out of the overall results, so it keeps articles that only
 * rank well among their neighbours.
 */
const SearchRes = ({ location }) => {
  const params = new URLSearchParams(location.search);
  const keyword = (params.get("keyword") || "").trim();
  const category = params.get("category") ? findCategory(params.get("category")) : null;
  const categorySlug = category ? category.slug : null;

  const allResults = searchArticles(keyword, null);
  const results = categorySlug ? searchArticles(keyword, categorySlug) : allResults;
  const facets = facetsFor(allResults, categorySlug);

  // One event per search, with its result count: the zero-result searches are
  // the list of questions the help center does not answer yet.
  const resultCount = results.length;
  useEffect(() => {
    if (keyword) trackHelpSearch(keyword, resultCount, categorySlug);
  }, [keyword, categorySlug, resultCount]);

  const crumbs = [{ label: "Help Center", to: "/" }];
  if (category) crumbs.push({ label: category.title, to: `/${category.slug}` });
  crumbs.push({ label: "Hasil pencarian" });

  let summary = "Ketik kata kunci untuk mulai mencari.";
  if (keyword) summary = `${resultCount} artikel cocok${category ? ` di ${category.title}` : ""}.`;

  return (
    <DocumentTitle title={`Cari "${keyword}" - Kyou Help Center`}>
      <div className="hc-page">
        <header className="hc-cathead">
          <div className="hc-container">
            <Breadcrumbs items={crumbs} />
            <div className="hc-cathead__row">
              <div>
                <h1 className="hc-cathead__title">{keyword ? `Hasil pencarian: ${keyword}` : "Cari di Help Center"}</h1>
                <p className="hc-cathead__sub">{summary}</p>
              </div>
              <HelpSearch variant="compact" category={category} initialValue={keyword} key={location.search} />
            </div>
          </div>
        </header>

        {keyword ? (
          <div className="hc-container">
            <div className="hc-split">
              {allResults.length > 0 ? (
                <nav className="hc-rail" aria-label="Saring menurut kategori">
                  <span className="hc-rail__label">Kategori</span>
                  <Link
                    to={searchPath(keyword, null)}
                    className={`hc-rail__item${categorySlug ? "" : " hc-rail__item--active"}`}
                    aria-current={categorySlug ? undefined : "page"}
                  >
                    <span className="hc-rail__name">Semua kategori</span>
                    <span className="hc-rail__n">{allResults.length}</span>
                  </Link>
                  {facets.map(facet => (
                    <Link
                      key={facet.slug}
                      to={searchPath(keyword, facet.slug)}
                      className={`hc-rail__item${facet.slug === categorySlug ? " hc-rail__item--active" : ""}`}
                      aria-current={facet.slug === categorySlug ? "page" : undefined}
                    >
                      <span className="hc-rail__name">{facet.title}</span>
                      <span className="hc-rail__n">{facet.slug === categorySlug ? resultCount : facet.count}</span>
                    </Link>
                  ))}
                </nav>
              ) : null}

              <div className="hc-split__main">
                {results.length > 0 ? (
                  <ul className="hc-results">
                    {results.map(({ article, snippet }) => (
                      <li key={article.id}>
                        <Link to={`/${article.id}`} className="hc-result">
                          <span className="hc-result__body">
                            <span className="hc-result__cat">{article.category.title}</span>
                            <span className="hc-result__title">{article.title}</span>
                            <Snippet parts={snippet} className="hc-result__snippet" />
                          </span>
                          <span className="hc-result__arrow">
                            <ArrowRightIcon />
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="hc-topic hc-empty">
                    <h2 className="hc-topic__head">Belum ketemu &ldquo;{keyword}&rdquo;</h2>
                    <p className="hc-topic__sub">
                      Coba kata lain yang lebih umum, misalnya &ldquo;refund&rdquo; atau &ldquo;ongkir&rdquo;
                      {category ? ", atau cari di semua kategori" : ""}. Kalau tetap belum ketemu, Team Kyou siap bantu lewat chat.
                    </p>
                    <div className="hc-empty__actions">
                      {category ? (
                        <Link className="hc-btn hc-btn--ghost" to={searchPath(keyword, null)}>
                          Cari di semua kategori
                        </Link>
                      ) : null}
                      <a
                        className="hc-btn"
                        href={getCsWhatsAppUrl(`Halo Kyou, aku mau tanya soal ${keyword}.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Chat Team Kyou
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </DocumentTitle>
  );
};

export default SearchRes;
